import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Switch,
  FormControlLabel,
  Chip,
  Alert,
  Tabs,
  Tab,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import { Add, Edit, Delete, Category as CategoryIcon, Discount, ProductionQuantityLimits } from '@mui/icons-material';
import kitsService from '../services/kitsService';
import productsService from '../services/productsService';

const KitsManagement = () => {
  const [kits, setKits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // États pour le modal de création/édition de kit
  const [openKitDialog, setOpenKitDialog] = useState(false);
  const [currentKit, setCurrentKit] = useState(null);
  const [kitForm, setKitForm] = useState({
    name: '',
    description: '',
    image: '',
    is_active: true,
    display_order: 0,
  });

  // États pour la gestion des catégories
  const [selectedKit, setSelectedKit] = useState(null);
  const [currentTab, setCurrentTab] = useState(0);
  const [openCategoryDialog, setOpenCategoryDialog] = useState(false);
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    is_required: true,
    allow_multiple: false,
    min_selection: 1,
    max_selection: null,
    display_order: 0,
  });

  // États pour les produits
  const [openProductDialog, setOpenProductDialog] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [productSearchLoading, setProductSearchLoading] = useState(false);
  const [productForm, setProductForm] = useState({
    product_id: '',
    allow_all_variants: true,
    allowed_variant_ids: [],
    is_default: false,
    display_order: 0,
  });
  const [selectedProductVariants, setSelectedProductVariants] = useState([]);

  // États pour les remises
  const [openDiscountDialog, setOpenDiscountDialog] = useState(false);
  const [discountForm, setDiscountForm] = useState({
    quantity: 2,
    discount_type: 'percentage',
    discount_percentage: 10,
    discount_amount: null,
  });

  useEffect(() => {
    loadKits();
  }, []);

  const loadKits = async () => {
    try {
      setLoading(true);
      const data = await kitsService.getAllKits();
      setKits(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchProducts = async (searchText) => {
    if (!searchText || searchText.length < 2) {
      setAvailableProducts([]);
      return;
    }

    try {
      setProductSearchLoading(true);
      const data = await productsService.searchProducts(searchText);
      setAvailableProducts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Erreur lors de la recherche des produits:', err);
      setAvailableProducts([]);
    } finally {
      setProductSearchLoading(false);
    }
  };

  const handleProductSelect = (product) => {
    if (product) {
      setProductForm({ 
        ...productForm, 
        product_id: product.id,
      });
      // Si le produit a des variantes, les charger
      if (product.variants && product.variants.length > 0) {
        setSelectedProductVariants(product.variants);
      } else {
        setSelectedProductVariants([]);
      }
    } else {
      setProductForm({ 
        ...productForm, 
        product_id: '',
      });
      setSelectedProductVariants([]);
    }
  };

  const handleCreateKit = () => {
    setCurrentKit(null);
    setKitForm({
      name: '',
      description: '',
      image: '',
      is_active: true,
      display_order: 0,
    });
    setOpenKitDialog(true);
  };

  const handleEditKit = (kit) => {
    setCurrentKit(kit);
    setKitForm({
      name: kit.name,
      description: kit.description || '',
      image: kit.image || '',
      is_active: kit.is_active,
      display_order: kit.display_order,
    });
    setOpenKitDialog(true);
  };

  const handleSaveKit = async () => {
    try {
      if (currentKit) {
        await kitsService.updateKit(currentKit.id, kitForm);
        setSuccess('Kit mis à jour avec succès');
      } else {
        await kitsService.createKit(kitForm);
        setSuccess('Kit créé avec succès');
      }
      setOpenKitDialog(false);
      loadKits();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteKit = async (kitId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce kit ?')) {
      return;
    }

    try {
      await kitsService.deleteKit(kitId);
      setSuccess('Kit supprimé avec succès');
      loadKits();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAddCategory = async () => {
    if (!selectedKit) return;

    try {
      await kitsService.addCategory(selectedKit.id, categoryForm);
      setSuccess('Catégorie ajoutée avec succès');
      setOpenCategoryDialog(false);
      loadKits();
      // Recharger le kit sélectionné
      const updatedKit = await kitsService.getKit(selectedKit.id);
      setSelectedKit(updatedKit);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAddProduct = async () => {
    if (!selectedCategory) return;

    try {
      await kitsService.addProductToCategory(selectedCategory.id, productForm);
      setSuccess('Produit ajouté avec succès');
      setOpenProductDialog(false);
      loadKits();
      // Recharger le kit sélectionné
      const updatedKit = await kitsService.getKit(selectedKit.id);
      setSelectedKit(updatedKit);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAddDiscount = async () => {
    if (!selectedCategory) return;

    try {
      await kitsService.addDiscountToCategory(selectedCategory.id, discountForm);
      setSuccess('Remise ajoutée avec succès');
      setOpenDiscountDialog(false);
      loadKits();
      // Recharger le kit sélectionné
      const updatedKit = await kitsService.getKit(selectedKit.id);
      setSelectedKit(updatedKit);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRemoveProduct = async (categoryId, productId) => {
    try {
      await kitsService.removeProductFromCategory(categoryId, productId);
      setSuccess('Produit supprimé avec succès');
      loadKits();
      // Recharger le kit sélectionné
      if (selectedKit) {
        const updatedKit = await kitsService.getKit(selectedKit.id);
        setSelectedKit(updatedKit);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRemoveDiscount = async (categoryId, discountId) => {
    try {
      await kitsService.removeDiscountFromCategory(categoryId, discountId);
      setSuccess('Remise supprimée avec succès');
      loadKits();
      // Recharger le kit sélectionné
      if (selectedKit) {
        const updatedKit = await kitsService.getKit(selectedKit.id);
        setSelectedKit(updatedKit);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRemoveCategory = async (categoryId) => {
    if (!selectedKit) return;
    
    try {
      await kitsService.removeCategory(selectedKit.id, categoryId);
      setSuccess('Catégorie supprimée avec succès');
      loadKits();
      // Recharger le kit sélectionné
      if (selectedKit) {
        const updatedKit = await kitsService.getKit(selectedKit.id);
        setSelectedKit(updatedKit);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleViewKitDetails = async (kit) => {
    try {
      const fullKit = await kitsService.getKit(kit.id);
      setSelectedKit(fullKit);
      setCurrentTab(0);
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Gestion des Kits Personnalisables</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          onClick={handleCreateKit}
        >
          Créer un Kit
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Liste des kits */}
        <Grid item xs={12} md={selectedKit ? 6 : 12}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Kits ({kits.length})
              </Typography>

              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Nom</TableCell>
                    <TableCell>Ordre</TableCell>
                    <TableCell>Actif</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {kits.map((kit) => (
                    <TableRow 
                      key={kit.id}
                      sx={{ 
                        cursor: 'pointer',
                        bgcolor: selectedKit?.id === kit.id ? 'action.selected' : 'inherit'
                      }}
                      onClick={() => handleViewKitDetails(kit)}
                    >
                      <TableCell>{kit.name}</TableCell>
                      <TableCell>{kit.display_order}</TableCell>
                      <TableCell>
                        <Chip
                          label={kit.is_active ? 'Actif' : 'Inactif'}
                          color={kit.is_active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEditKit(kit); }}>
                          <Edit />
                        </IconButton>
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDeleteKit(kit.id); }}>
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>

        {/* Détails du kit sélectionné */}
        {selectedKit && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {selectedKit.name}
                </Typography>

                <Tabs value={currentTab} onChange={(e, v) => setCurrentTab(v)}>
                  <Tab label="Catégories" />
                  <Tab label="Remises Progressives" />
                  <Tab label="Configuration" />
                </Tabs>

                {/* Onglet Catégories */}
                {currentTab === 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="subtitle1">Catégories</Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<CategoryIcon />}
                        onClick={() => {
                          setCategoryForm({
                            name: '',
                            description: '',
                            is_required: true,
                            allow_multiple: false,
                            min_selection: 1,
                            max_selection: null,
                            display_order: 0,
                          });
                          setOpenCategoryDialog(true);
                        }}
                      >
                        Ajouter une catégorie
                      </Button>
                    </Box>

                    {selectedKit.categories?.map((category) => (
                      <Card key={category.id} sx={{ mb: 2, p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                              {category.name}
                              {category.is_required && <Chip label="Obligatoire" size="small" color="error" sx={{ ml: 1 }} />}
                              {category.allow_multiple && <Chip label="Multiple" size="small" color="info" sx={{ ml: 1 }} />}
                            </Typography>
                            <Typography variant="caption" display="block" sx={{ mb: 2 }}>
                              {category.description}
                            </Typography>
                          </Box>
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => handleRemoveCategory(category.id)}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Box>

                        {/* Produits de la catégorie */}
                        <Box sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                              Produits ({category.products?.length || 0})
                            </Typography>
                            <Button
                              size="small"
                              variant="text"
                              startIcon={<ProductionQuantityLimits />}
                              onClick={() => {
                                setSelectedCategory(category);
                                setProductForm({
                                  product_id: '',
                                  allow_all_variants: true,
                                  allowed_variant_ids: [],
                                  is_default: false,
                                  display_order: 0,
                                });
                                setAvailableProducts([]);
                                setSelectedProductVariants([]);
                                setOpenProductDialog(true);
                              }}
                            >
                              Ajouter
                            </Button>
                          </Box>
                          {category.products?.map((catProduct) => (
                            <Box key={catProduct.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.75rem', mb: 0.5 }}>
                              <span>
                                {catProduct.product?.name}
                                {catProduct.is_default && <Chip label="Défaut" size="small" sx={{ ml: 1, height: 16 }} />}
                              </span>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <span>{catProduct.custom_price_ttc || catProduct.product?.priceTtc}€</span>
                                <IconButton 
                                  size="small" 
                                  color="error"
                                  onClick={() => handleRemoveProduct(category.id, catProduct.id)}
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Box>
                            </Box>
                          ))}
                        </Box>

                        {/* Remises */}
                        <Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                              Remises ({category.discounts?.length || 0})
                            </Typography>
                            <Button
                              size="small"
                              variant="text"
                              startIcon={<Discount />}
                              onClick={() => {
                                setSelectedCategory(category);
                                setDiscountForm({
                                  quantity: 2,
                                  discount_type: 'percentage',
                                  discount_percentage: 10,
                                  discount_amount: null,
                                });
                                setOpenDiscountDialog(true);
                              }}
                            >
                              Ajouter
                            </Button>
                          </Box>
                          {category.discounts?.map((discount) => (
                            <Box key={discount.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.75rem', mb: 0.5 }}>
                              <span>
                                {discount.quantity} produits : -{discount.discountType === 'percentage' 
                                  ? `${discount.discountPercentage}%` 
                                  : `${discount.discountAmount}€`}
                              </span>
                              <IconButton 
                                size="small" 
                                color="error"
                                onClick={() => handleRemoveDiscount(category.id, discount.id)}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Box>
                          ))}
                        </Box>
                      </Card>
                    ))}
                  </Box>
                )}

                {/* Onglet Remises Progressives */}
                {currentTab === 1 && (
                  <Box sx={{ mt: 2 }}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Les remises progressives permettent de définir un pourcentage de réduction différent pour chaque quantité (1→0%, 2→5%, 3→10%, etc.)
                    </Alert>
                    {selectedKit.categories?.map((category) => (
                      category.allow_multiple && (
                        <Box key={category.id} sx={{ mb: 3 }}>
                          <Card>
                            <CardContent>
                              <Typography variant="h6" sx={{ mb: 2 }}>
                                {category.name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Remises actuelles : {category.discounts?.length || 0}
                              </Typography>
                              {category.discounts?.map((discount) => (
                                <Box key={discount.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                  <Typography variant="body2">
                                    {discount.quantity} produit{discount.quantity > 1 ? 's' : ''} : 
                                    <strong> -{discount.discountPercentage}%</strong>
                                  </Typography>
                                  <IconButton 
                                    size="small" 
                                    color="error"
                                    onClick={() => handleRemoveDiscount(category.id, discount.id)}
                                  >
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Box>
                              ))}
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<Discount />}
                                onClick={() => {
                                  setSelectedCategory(category);
                                  setDiscountForm({
                                    quantity: (category.discounts?.length || 0) + 1,
                                    discount_type: 'percentage',
                                    discount_percentage: 10,
                                    discount_amount: null,
                                  });
                                  setOpenDiscountDialog(true);
                                }}
                                sx={{ mt: 2 }}
                              >
                                Ajouter une remise
                              </Button>
                            </CardContent>
                          </Card>
                        </Box>
                      )
                    ))}
                    {!selectedKit.categories?.some(c => c.allow_multiple) && (
                      <Alert severity="info">
                        Aucune catégorie avec sélection multiple.
                        Les remises progressives ne s'appliquent qu'aux catégories avec sélection multiple activée.
                      </Alert>
                    )}
                  </Box>
                )}

                {/* Onglet Configuration */}
                {currentTab === 2 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Description :</strong> {selectedKit.description || 'Aucune'}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Image :</strong> {selectedKit.image || 'Aucune'}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Ordre d'affichage :</strong> {selectedKit.display_order}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Dialog de création/édition de kit */}
      <Dialog open={openKitDialog} onClose={() => setOpenKitDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{currentKit ? 'Modifier le kit' : 'Créer un kit'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Nom du kit"
              value={kitForm.name}
              onChange={(e) => setKitForm({ ...kitForm, name: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Description"
              value={kitForm.description}
              onChange={(e) => setKitForm({ ...kitForm, description: e.target.value })}
              multiline
              rows={3}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="URL de l'image"
              value={kitForm.image}
              onChange={(e) => setKitForm({ ...kitForm, image: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              type="number"
              label="Ordre d'affichage"
              value={kitForm.display_order}
              onChange={(e) => setKitForm({ ...kitForm, display_order: parseInt(e.target.value) })}
              sx={{ mb: 2 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={kitForm.is_active}
                  onChange={(e) => setKitForm({ ...kitForm, is_active: e.target.checked })}
                />
              }
              label="Actif"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenKitDialog(false)}>Annuler</Button>
          <Button onClick={handleSaveKit} variant="contained">
            {currentKit ? 'Modifier' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog d'ajout de catégorie */}
      <Dialog open={openCategoryDialog} onClose={() => setOpenCategoryDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Ajouter une catégorie</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Nom de la catégorie"
              value={categoryForm.name}
              onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
              placeholder="ex: Machines, Aiguilles, Encres..."
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Description"
              value={categoryForm.description}
              onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
              multiline
              rows={2}
              sx={{ mb: 2 }}
            />
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Sélection minimum"
                  value={categoryForm.min_selection}
                  onChange={(e) => setCategoryForm({ ...categoryForm, min_selection: parseInt(e.target.value) })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Sélection maximum"
                  value={categoryForm.max_selection || ''}
                  onChange={(e) => setCategoryForm({ ...categoryForm, max_selection: e.target.value ? parseInt(e.target.value) : null })}
                />
              </Grid>
            </Grid>
            <FormControlLabel
              control={
                <Switch
                  checked={categoryForm.is_required}
                  onChange={(e) => setCategoryForm({ ...categoryForm, is_required: e.target.checked })}
                />
              }
              label="Catégorie obligatoire"
              sx={{ mb: 1 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={categoryForm.allow_multiple}
                  onChange={(e) => setCategoryForm({ ...categoryForm, allow_multiple: e.target.checked })}
                />
              }
              label="Autoriser sélection multiple"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCategoryDialog(false)}>Annuler</Button>
          <Button onClick={handleAddCategory} variant="contained">
            Ajouter
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog d'ajout de produit */}
      <Dialog open={openProductDialog} onClose={() => setOpenProductDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Ajouter un produit à la catégorie</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Autocomplete
              options={availableProducts}
              getOptionLabel={(option) => `${option.name || ''} - ${option.priceTtc || 0}€`}
              loading={productSearchLoading}
              onInputChange={(event, newInputValue) => {
                handleSearchProducts(newInputValue);
              }}
              onChange={(event, newValue) => {
                handleProductSelect(newValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Rechercher un produit"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {productSearchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              sx={{ mb: 2 }}
            />

            <Alert severity="info" sx={{ mb: 2 }}>
              Les prix du produit seront automatiquement récupérés depuis la base de données
            </Alert>

            {/* Affichage de la gestion des variantes si le produit en a */}
            {selectedProductVariants.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={productForm.allow_all_variants}
                      onChange={(e) => setProductForm({ ...productForm, allow_all_variants: e.target.checked })}
                    />
                  }
                  label="Autoriser toutes les variantes"
                />
                
                {!productForm.allow_all_variants && (
                  <Autocomplete
                    multiple
                    options={selectedProductVariants}
                    getOptionLabel={(option) => `${option.name || ''} - ${option.priceTtc || 0}€`}
                    value={selectedProductVariants.filter(v => productForm.allowed_variant_ids.includes(v.id))}
                    onChange={(event, newValues) => {
                      setProductForm({ 
                        ...productForm, 
                        allowed_variant_ids: newValues.map(v => v.id) 
                      });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Sélectionner les variantes autorisées"
                        placeholder="Choisir les variantes"
                      />
                    )}
                    sx={{ mt: 2 }}
                  />
                )}
              </Box>
            )}

            <FormControlLabel
              control={
                <Switch
                  checked={productForm.is_default}
                  onChange={(e) => setProductForm({ ...productForm, is_default: e.target.checked })}
                />
              }
              label="Sélectionné par défaut"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenProductDialog(false)}>Annuler</Button>
          <Button onClick={handleAddProduct} variant="contained">
            Ajouter
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog d'ajout de remise */}
      <Dialog open={openDiscountDialog} onClose={() => setOpenDiscountDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Ajouter une remise</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              type="number"
              label="Nombre de produits minimum"
              value={discountForm.quantity}
              onChange={(e) => setDiscountForm({ ...discountForm, quantity: parseInt(e.target.value) })}
              helperText="Nombre de produits à sélectionner pour bénéficier de la remise"
              sx={{ mb: 2 }}
            />

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Type de remise</InputLabel>
              <Select
                value={discountForm.discount_type}
                onChange={(e) => setDiscountForm({ ...discountForm, discount_type: e.target.value })}
                label="Type de remise"
              >
                <MenuItem value="percentage">Pourcentage</MenuItem>
                <MenuItem value="fixed">Montant fixe</MenuItem>
              </Select>
            </FormControl>

            {discountForm.discount_type === 'percentage' ? (
              <TextField
                fullWidth
                type="number"
                label="Pourcentage de remise"
                value={discountForm.discount_percentage}
                onChange={(e) => setDiscountForm({ ...discountForm, discount_percentage: parseFloat(e.target.value) })}
                inputProps={{ step: 0.1, min: 0, max: 100 }}
                helperText="Pourcentage de réduction (0-100%)"
              />
            ) : (
              <TextField
                fullWidth
                type="number"
                label="Montant de remise"
                value={discountForm.discount_amount}
                onChange={(e) => setDiscountForm({ ...discountForm, discount_amount: parseFloat(e.target.value) })}
                inputProps={{ step: 0.01 }}
                helperText="Montant fixe de réduction en €"
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDiscountDialog(false)}>Annuler</Button>
          <Button onClick={handleAddDiscount} variant="contained">
            Ajouter
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default KitsManagement;

